import React from 'react';

import {ResponsiveImage, Footnotes, Footnote, Ref, Code} from '../util.js';

import BaseProb from './images/base_prob-fs8.png';
import CondProb from './images/cond_prob-fs8.png';
import ExpResults from './images/exp_results-fs8.png';

export default function() {
    return <>
        <h2>Problem Statement</h2>
        <p>
            Choose an integer uniformly at random from the interval \([N, N + L]\), where \(N \gg L\).
            Given that the integer \(N\) has no prime factors in an interval \([B_0, B_1]\),
            what is the (conditional) probability that it is \(B\)-smooth?
        </p>
        <p>This problem can be easily extended to e.g. restrict prime factors to multiple subintervals.
        For simplicity, we will analyze the single-interval problem above.
        In what follows, we assume that {String.raw`\(B_0 < B_1 < B\)`}.
        </p>

        <h2>Solution</h2>
        <p>
            Let \(K(a, b) \subseteq [a, b]\) denote the set of \(B\)-smooth integers in the interval \([a, b]\).
            Observe that the set of integers in \(K(a, b)\) divisible by a fixed prime \(p\)
            is in bijection with the set \(K(a/p, b/p)\).
        </p>
        <p>
            By the principle of inclusion-exclusion (PIE), the number \(M\) of integers in \(K(N, N+L)\) which are not divisible by any \(p \in [B_0, B_1]\) is
            {String.raw`\[
            \begin{aligned}
                M = \, &|K(N, N+L)| \\
                &- \sum_{B_0 \leq p \leq B_1} |K(N/p, (N+L)/p)| \\
                &+ \sum_{B_0 \leq p_1 < p_2 \leq B_1} |K(N/(p_1 p_2), (N+L)/(p_1 p_2))| \\
                &- \cdots
                \end{aligned}
            \]`}
            Recall that \(K(a, b) \sim (b-a) \, \rho(\ln(a)/\ln(B))\) for intervals with \(b-a \ll a\), where \(\rho\) is the Dickman-de Brujin function.
            We therefore have
            {String.raw`\[
            \begin{aligned}
                \frac{M}{L} \sim \, &\rho(\ln{N}/\ln{B}) \\
                &- \sum_{B_0 \leq p \leq B_1} \rho\left(\frac{\ln{N} - \ln{p}}{\ln{B}}\right) \\
                &+ \sum_{B_0 \leq p_1 < p_2 \leq B_1} \rho\left(\frac{\ln{N} - \ln{p_1} - \ln{p_2}}{\ln{B}}\right) \\
                &- \cdots
                \end{aligned}
            \]`}
            Using the heuristic measure
            {String.raw`\[\mathbb{1}_{\text{primes}} \sim \frac{dx}{x \ln{x}} \sim \frac{du}{u}\]`}
            where {String.raw`\(u := \ln{x}\)`}, we may write
            {String.raw`\[
            \begin{aligned}
                \frac{M}{L} \sim \, &\rho(\ln{N}/\ln{B}) \\
                &- \int_{\ln{B_0}}^{\ln{B_1}} \frac{1}{u} \, \rho\left(\frac{\ln{N} - u}{\ln{B}}\right) \dd{u} \\
                &+ \frac{1}{2!} \int_{\ln{B_0}}^{\ln{B_1}} \int_{\ln{B_0}}^{\ln{B_1}} \frac{1}{uv} \, \rho\left(\frac{\ln{N} - u - v}{\ln{B}}\right) \dd{u} \dd{v} \\
                &- \cdots
                \end{aligned}
            \]`}
            This is a bit unwieldy, and the multidimensional integrals are difficult to compute numerically.
            Luckily, it's possible to simplify this quite a bit.
        </p>
        <h2>Simplification</h2>
        <p>Recursively define the helper function
            {String.raw`\[
                I_0(x) := \rho(x/\ln{B}),
            \]
            \[
                I_n(x) := \frac{1}{n} \int_{\ln{B_0}}^{\ln{B_1}} \frac{I_{n-1}(x-u)}{u} \, \dd{u}.
            \]`}
            Then we have the much nicer expression
            {String.raw`\[
                \mathbb{P}(\text{clean and }B\text{-smooth}) := \frac{M}{L} = \sum_{n=0}^\infty (-1)^n \, I_n(\ln{N}),
            \]`}
            where I have defined <i>clean</i> to be the property of having no prime factors in the interval \([B_0, B_1]\), and <i>smooth</i> to mean \(B\)-smooth.
        </p>
        <p>
            The functions \(I_n\) are easily computed iteratively over \(n\),
            by keeping track of all values over the interval {String.raw`\(-1 < x \leq \ln{N}\)`}.
            To obtain correct results, it is important to use the convention \(\rho(x) = 0\) for {String.raw`\(x < 0\)`} to remain consistent with the original formula from PIE.
        </p>
        <p>
            Finally, we get the desired conditional probability
            {String.raw`\[
                \mathbb{P}(B\text{-smooth} \, | \, \text{clean}) = \frac{\mathbb{P}(\text{clean and }B\text{-smooth})}{\mathbb{P}(\text{clean and }\infty\text{-smooth})},
            \]`}
            where both the numerator and denominator can be computed using the formula above.<Ref id={1}/>
        </p>

        <h2>Numerical Computation</h2>
        <p>The below plot shows the quantities {String.raw`\(\mathbb{P}(B\text{-smooth})\)`}, {String.raw`\(\mathbb{P}(\text{clean})\)`}, and {String.raw`\(\mathbb{P}(\text{clean and }B\text{-smooth})\)`} for concrete values {String.raw`\((B_0, B_1, B) = 2^{20}, 2^{32}, 2^{37}\)`}.
        </p>
        <ResponsiveImage
            desktopSource={BaseProb}
            mobileSource={BaseProb}
            alt='Graph showing probabilities of being B-smooth and/or clean as a function of log(N).'
        />
        <p>Note that the slight wiggles in the purple curve are real and not numerical artifacts!</p>
        <p>As \(N \to \infty\), the probability {String.raw`\(\mathbb{P}(\text{clean})\)`} tends towards the value {String.raw`\(\ln{B_0}/\ln{B_1}\)`}. This is consistent with Mertens’ third theorem.</p>
        <p>From the above curves, we can also compute the conditional probability of interest. As expected, the probability of a clean integer being \(B\)-smooth is lower than that of a generic integer.</p>
        <ResponsiveImage
            desktopSource={CondProb}
            mobileSource={CondProb}
            alt='Graph showing conditional probability of being B-smooth given clean as a function of log(N).'
        />
        <p>Finally, we can experimentally generate clean integers<Ref id={2}/> and plot the probability of being \(B\)-smooth for various values of \(B\), and compare against the theoretical reference.</p>

        <ResponsiveImage
            desktopSource={ExpResults}
            mobileSource={ExpResults}
            alt='Experimental results agree very well with theoretical prediction.'
        />

        <p>We get excellent agreement, even down to the subtle wiggles in the theoretical prediction.</p>

        <Footnotes>
            <Footnote id={1}>
                Note that <b>all</b> integers are \(\infty\)-smooth.
            </Footnote>
            <Footnote id={2}>
                This can be done efficiently using a sieve over primes \(p \in [B_0, B_1]\) and removing the sieved part.
                The remaining cofactors are all clean values of \(N\).
            </Footnote>
        </Footnotes>
    </>
}
